import {
  DOCUMENT,
  Inject,
  inject,
  Injectable,
  NgZone,
  Optional,
  Renderer2,
  RendererFactory2,
  SecurityContext,
} from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { catchError, from, Observable, of, switchMap, tap } from 'rxjs';
import { LayoutModel } from '@app/models/server/layout.model';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import { StorageUtilLibService } from '@b2b/storage-util-lib';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '@src/environments/environment';
import { delay, map } from 'rxjs/operators';
import { SeoDbSchema } from '../../models/seo-db.schema';
import { SeoUrlSchema } from '../../models/seo-url.schema';
import { JsonLd, JsonLdService } from '../json-ld/json-ld.service';
import { SeoSocialShareData } from '../../models/seo-social-share-data';
import { SeoSocialShareService } from '../seo-social-share/seo-social-share.service';
import { APP_TYPE_CONSTANTS } from '@app/constants/app-settings';
import { HttpClientService } from '@app/api-v2/util/http/http-client.service';

@Injectable({
  providedIn: 'root',
})
export class SeoSetupService {
  renderer: Renderer2;

  private httpClientService: HttpClientService;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private rendererFactory: RendererFactory2,
    protected sanitizer: DomSanitizer,
    private storageUtilLibService: StorageUtilLibService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    @Optional() private readonly seoSocialShareService: SeoSocialShareService,
    @Optional() private readonly jsonLdService: JsonLdService,
  ) {
    this.httpClientService = inject(HttpClientService);
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  initSeoData(): Observable<LayoutModel> {
    let urlPath: string = this.router.url.toLowerCase();
    let splitUrl: string[] = [];
    // let queryString: string;
    if (urlPath.includes('?')) {
      // urlPath = urlPath.substring(0, urlPath.indexOf('?'));
      splitUrl = urlPath?.split('?');
      urlPath = splitUrl[0];

      /*if (splitUrl[1]) {
        queryString = `${splitUrl[1]}&path=${urlPath}`;
      }*/
    }

    return this.getSeoData(
      urlPath,
      this.activatedRoute?.snapshot?.queryParams,
    ).pipe(
      switchMap((result: LayoutModel) => {
        if (this.b2bDeviceUtilService.isServer()) {
          this.updateDeviceType(result);
          this.updateSeoData(result);
        }

        return of(result);
      }),
      tap((result: LayoutModel) => {
        if (!this.b2bDeviceUtilService.isServer()) {
          if (
            this.document?.getElementById('seo-data')?.innerText?.trim() === ''
          ) {
            this.updateSeoData(result);
          }
        }
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse?.status === 404) {
          this.router.navigateByUrl('/page-not-found').then();
        }
        return of({});
      }),
    );
  }

  updateDeviceType(layoutModel: LayoutModel) {
    const divElement = this.document?.getElementById('detect-device');
    if (divElement) {
      if (layoutModel?.appType === 'WEB_BROWSER') {
        divElement.innerText = 'WEB_BROWSER_DETECTED';
      } else if (layoutModel?.appType === 'MOBILE_BROWSER') {
        divElement.innerText = 'MOBILE_BROWSER_DETECTED';
      } else {
        divElement.innerText = 'NOTHING_DETECTED - ' + layoutModel?.appType;
      }
    }
  }

  getSeoData(path: string, queryParams?: Params): Observable<LayoutModel> {
    if (environment.mobileApp) {
      return this.getSeoDataFromCache(path);
    } else {
      return this.getSeoDataFromApi(path, queryParams);
    }
  }

  getSeoDataFromApi(path: string, params?: Params) {
    let httpParams = new HttpParams();

    if (params && Object.entries(params)?.length > 0) {
      httpParams = httpParams.appendAll(params);
    }
    httpParams = httpParams.append('path', path);
    return this.httpClientService.get(`load-container`, {
      params: httpParams,
    });
  }

  getSeoDataFromCache(path: string): Observable<LayoutModel> {
    let cacheKey: string;

    if (!this.b2bDeviceUtilService.isDesktop()) {
      cacheKey =
        STORAGE_CONST.MOBILE_PAGE_DATA_CACHE_PREFIX_ENUM +
        STORAGE_CONST.SEO_CACHE_PREFIX_ENUM +
        path?.toLowerCase() +
        STORAGE_CONST.SEO_CACHE_SUFFIX_ENUM;
    } else {
      cacheKey =
        STORAGE_CONST.DESKTOP_PAGE_DATA_CACHE_PREFIX_ENUM +
        STORAGE_CONST.SEO_CACHE_PREFIX_ENUM +
        path?.toLowerCase() +
        STORAGE_CONST.SEO_CACHE_SUFFIX_ENUM;
    }

    return from(this.storageUtilLibService.get(cacheKey, false)).pipe(
      switchMap((cacheData: LayoutModel) => {
        /*LoggerService.log('cacheData ', cacheData);*/
        if (cacheData?.mainContainer?.length > 0) {
          return of(cacheData);
        } else {
          /*return this.httpClientService
            .post(`load-container?path=${path}`, { path }, {})
            .pipe(
              tap((res: LayoutModel) => {
                if (res?.seoPageData?.pageType) {
                  this.storageUtilLibService.set(cacheKey, res, false).then();
                }
              }),
            );*/

          let queryParams = new HttpParams();
          queryParams = queryParams.append('path', path);

          return this.httpClientService
            .get(`load-mobile-container`, { params: queryParams })
            .pipe(
              tap((res: LayoutModel) => {
                if (res?.seoPageData?.pageType) {
                  // this.storageUtilLibService.set(cacheKey, res, false).then();
                  if (
                    (res?.appType === APP_TYPE_CONSTANTS.MOBILE_APP ||
                      res?.appType === APP_TYPE_CONSTANTS.MOBILE_BROWSER) &&
                    !this.b2bDeviceUtilService.isDesktop()
                  ) {
                    this.storageUtilLibService.set(cacheKey, res, false).then();
                  }
                  if (
                    res?.appType === APP_TYPE_CONSTANTS.WEB_BROWSER &&
                    this.b2bDeviceUtilService.isDesktop()
                  ) {
                    this.storageUtilLibService.set(cacheKey, res, false).then();
                  }
                }
              }),
            );
        }
      }),
    );
  }

  public updateSeoData(layoutContainer: LayoutModel) {
    if (environment?.webApp) {
      if (!this.b2bDeviceUtilService.isServer()) {
        if (layoutContainer?.seoPageData?.seoUrl) {
          this.callWebWorkerToPrepareSeoData(layoutContainer);
        } else {
          this.processSeoUpdates(layoutContainer);
        }
      } else {
        this.processSeoUpdates(layoutContainer);
      }
    }
  }

  private setSeoData(seoData: string) {
    try {
      const element = this.renderer.selectRootElement(
        '.page-seo-data .seo-data',
        false,
      );

      const childDiv = this.renderer.createElement('div');

      if (seoData) {
        childDiv.innerHTML = this.sanitizer.sanitize(
          SecurityContext.HTML,
          this.sanitizer.bypassSecurityTrustHtml(seoData),
        );
      }

      this.renderer.appendChild(element, childDiv);
    } catch (e) {}
  }

  private setFooterLinksSeoData(seoData: string) {
    try {
      const element = this.renderer.selectRootElement(
        'footer .footer-links-seo-data',
        false,
      );

      const childDiv = this.renderer.createElement('div');

      if (seoData) {
        childDiv.innerHTML = this.sanitizer.sanitize(
          SecurityContext.HTML,
          this.sanitizer.bypassSecurityTrustHtml(seoData),
        );
      }

      this.renderer.appendChild(element, childDiv);
    } catch (e) {}
  }

  private callWebWorkerToPrepareSeoData(layoutContainer: LayoutModel) {
    this.ngZone.runOutsideAngular(() => {
      if (typeof Worker !== 'undefined') {
        /*const worker = new Worker(
          new URL('@app/bootstrap-apps/web-app.worker.ts', import.meta.url)
        );*/

        /*const worker = new Worker(
          new URL('../../../bootstrap-apps/web-app.worker.ts', import.meta.url)
        );*/

        try {
          const worker = new Worker(
            environment?.baseHref + 'assets/js/web-app1.worker.js',
          );
          worker.onmessage = ({ data }) => {
            this.ngZone.runOutsideAngular(() => {
              this.removeTags(data);
            });
          };

          worker.postMessage({
            layoutContainer,
          });
        } catch (e) {}
      }
    });
  }

  private removeTags(data) {
    if (data?.removeDefList?.length > 0) {
      from(data?.removeDefList)
        .pipe(
          tap((tag: string) => {
            this.seoSocialShareService.removeTag(tag);
          }),
        )
        .subscribe({
          next: (result) => {},
          complete: () => {
            this.addTags(data);
          },
        });
    } else {
      this.addTags(data);
    }
  }

  private addTags(data) {
    of({})
      .pipe(
        map((_temp) => {
          this.seoSocialShareService.updateAllMetaTags(
            data?.finalMetaDefList,
            true,
          );

          return {};
        }),
        delay(300),
        map((_temp) => {
          if (data?.seoDataText) {
            this.setSeoData(data?.seoDataText);
          }

          return {};
        }),
        delay(300),
        map((_temp) => {
          if (data?.fLinkSeoText) {
            this.setFooterLinksSeoData(data?.fLinkSeoText);
          }

          return {};
        }),
        delay(300),
        map((_temp) => {
          if (data?.jsonLds?.length > 0) {
            this.jsonLdService.setData(data?.jsonLds);
          }
          return {};
        }),
        delay(300),
        map((_temp) => {
          /*this.seoSocialShareService.setLanguageAlternativeUrl(
            'en-IN',
            data?.seoData?.url
          );*/

          this.seoSocialShareService.setData(data?.seoData);
          return {};
        }),
        /*delay(300),
        map((_temp) => {
          this.seoSocialShareService.setCanonicalUrl(data?.seoData?.url);
          return {};
        })*/
      )
      .subscribe();
  }

  /*  runOutSideAngular(layoutContainer: LayoutModel) {
      this.ngZone.runOutsideAngular(() => {
        this.processSeoUpdates(layoutContainer);
      });
    }*/

  resetSeoData() {
    const element = this.document?.getElementById('seo-data');
    if (element) {
      element.innerText = '';
    }
  }

  processSeoUpdates(layoutContainer: LayoutModel) {
    const metaData: SeoDbSchema = layoutContainer?.seoPageData;

    if (metaData?.seoUrl) {
      const seo: SeoUrlSchema = metaData.seoUrl;
      const jsonLds: JsonLd[] = [];
      if (metaData.companySchema) {
        jsonLds.push(metaData.companySchema);
      }
      if (metaData.faqSchema) {
        jsonLds.push(metaData.faqSchema);
      }
      if (metaData.breadCrumbSchema) {
        jsonLds.push(metaData.breadCrumbSchema);
      }
      if (metaData.hotelSchema) {
        jsonLds.push(metaData.hotelSchema);
      }

      this.jsonLdService.setData(jsonLds);

      const seoData: SeoSocialShareData = {
        pageTitle: seo.seoData.pageTitle,
        keywords: seo.seoData.keywords,
        descriptions: seo.seoData.descriptions,
        // TODO need to handle image from backend
        image: metaData?.image?.path,
        // TODO need to handle image from backend
        // author: environment.seo.author,
        canonicalUrl: seo?.seoData?.canonicalUrl,
        url: seo.seoData.url,
        type: 'website',
      };

      this.seoSocialShareService.setData(seoData);
      this.setSeoData(metaData.seoText);
      this.setFooterLinksSeoData(metaData.fLinkText);
      /*this.seoFooterSetupService.setH1TagAndParagraphs(
        seo.seoData.h1Tag,
        seo.paragraphs,
        seo.faqs
      );
      this.seoFooterSetupService.setInternalLinks(metaData.internalLinks);*/
    } else {
      // TODO if seo data is not available
      const jsonLd = this.jsonLdService.getObject('Website', {
        name: undefined,
        // TODO need to handle image from backend
        // url: environment.url,
      });
      this.jsonLdService.setData(jsonLd);
      const seoData: SeoSocialShareData = {
        pageTitle: undefined,
        descriptions: undefined,
        // TODO need to handle image from backend
        // image: environment.url + environment.seo.shareImg,
        // author: environment.seo.author,
        // url: environment.url,
        type: 'website',
      };
      this.seoSocialShareService.setData(seoData);
      this.setSeoData(undefined);
      this.setFooterLinksSeoData(undefined);
      // this.seoFooterSetupService.setH1TagAndParagraphs(undefined, [], []);
      // this.seoFooterSetupService.setInternalLinks([]);
    }
  }
}
